
import "./privacy-policy.css";
import { usePageTitle } from "../hooks/usePageTitle";
import React, { useState, useEffect } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import AnimatedText from "../components/AnimatedText";
import { ReactComponent as ArrowLeft } from "../icons/arrow-left.svg";
import { ReactComponent as Defending } from "../icons/defending.svg";

import _ from "lodash";

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.2,
        },
    },
};

const defendingVariants = {
    hidden: { opacity: 0, scale: 0.9, filter: "blur(24px)" },
    visible: {
        opacity: 1, scale: 1, filter: "blur(0px)", transition: {
            type: "spring",
            stiffness: 100,
            damping: 15,
            duration: 0.9,
            delay: 0.3,
        },
    },
};

function PrivacyPolicy() {

    usePageTitle("Foxer - Privacy Policy");

    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const sx = useSpring(x, { stiffness: 40, damping: 12 });
    const sy = useSpring(y, { stiffness: 40, damping: 12 });

    useEffect(() => {
        const handler = e => {
            const { innerWidth, innerHeight } = window;
            const nx = (e.clientX / innerWidth) * 2 - 1;
            const ny = (e.clientY / innerHeight) * 2 - 1;
            x.set(nx * 20);
            y.set(ny * 20);
        };
        window.addEventListener("mousemove", handler);
        return () => window.removeEventListener("mousemove", handler);
    }, []);

    return (
        <motion.div
            className="privacy-policy"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >

            <div className="privacy-policy-content">
                <button onClick={() => window.history.back()} className="privacy-policy-back-button">
                    <ArrowLeft />
                    Go back
                </button>
                <AnimatedText text="Privacy Policy" className="privacy-policy-title" tag="p" />
                <AnimatedText text="Version 2.0 - 24 Nov 2025" className="privacy-policy-subtitle" tag="p" />

                <div className="privacy-policy-text">
                    <AnimatedText text="1. Data We Collect" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        Foxer does not collect, request, or process any personal information. The app does not ask for your name, email address, phone number, or any other identifying details. The only data Foxer works with is the content you create inside the app itself, such as your tasks, their dates and times, their completion status, and any app preferences you configure (for example, theme or layout). All of this information is handled locally in your browser and is never sent to us.
                    </motion.p>

                    <AnimatedText text="2. Where Your Data Is Stored" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        All data used by Foxer is stored locally on your device via your browser&apos;s localStorage. We do not operate any database or remote storage for your Foxer data, and we do not mirror or sync your information to any server. This means that your tasks and preferences remain on the device and browser where you created them and do not leave that environment through Foxer.
                    </motion.p>

                    <AnimatedText text="3. No Accounts" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        Foxer does not use user accounts, logins, passwords, syncing, or cloud backups. This means your data stays on your device and cannot be accessed by us or anyone else through Foxer. There is no registration, no sign-in process, and no remote profile associated with your use of the app.
                    </motion.p>

                    <AnimatedText text="4. Limited Analytics" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        To keep improving Foxer, we use a basic analytics tool that shows us general usage trends-things like how many people visit and which parts of the site are used the most. It doesn’t look at your tasks, your preferences, or anything personal. Everything stays anonymous, and nothing is shared or sold.
                    </motion.p>

                    <AnimatedText text="5. Offline Usage" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        Foxer is designed to work fully offline. An internet connection is not required to create, edit, or complete tasks. If you visit a website where Foxer is hosted, the web host may collect standard technical logs for security or performance reasons (such as IP address, browser type, or request metadata), but this logging is separate from the app itself, and Foxer does not send your task data or content to any server.
                    </motion.p>

                    <AnimatedText text="6. Third-Party Libraries" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        Foxer may use third-party frontend libraries, such as Framer Motion for animations or Chrono (or similar libraries) for natural language date and time detection. These libraries run locally in your browser as part of the app code and are not used to send your data to external services. They operate only on the data already present in your browser and do not transmit it out of your device.
                    </motion.p>

                    <AnimatedText text="7. Data Deletion" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        Because Foxer stores data only on your device, you are in full control of deletion. You can remove all Foxer data at any time by clearing your browser&apos;s localStorage for the site where Foxer runs, by clearing site data in your browser settings. Once you delete this data, it is permanently removed and cannot be restored by us, as we do not keep any copy on remote servers.
                    </motion.p>

                    <AnimatedText text="8. Children&apos;s Privacy" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        Foxer does not collect personal information and does not require any form of registration, so it does not knowingly collect data from children or adults. The app can be used by people of any age, and since no personal data is transmitted to us, there is no central storage of information about children.
                    </motion.p>

                    <AnimatedText text="9. Changes to This Policy" className="privacy-policy-text-header" tag="p" />
                    <motion.p
                        className="privacy-policy-text-item"
                        initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ type: "spring", stiffness: 50, damping: 15, delay: 0.2 }}
                    >
                        We may update this privacy policy if Foxer gains new features or if our data practices change. When changes are made, the updated version will be published where the policy is displayed, and the &quot;Last updated&quot; date at the top will be revised. Continued use of Foxer after changes to this policy means you accept the updated version.
                    </motion.p>
                    <p className="privacy-policy-end">© 2025 Foxer. All rights reserved.</p>
                </div>
            </div>
            <motion.div
                variants={defendingVariants}
                initial="hidden"
                animate="visible"
                className="privacy-policy-defending-container"
            >
                <motion.div
                    style={{ x: sx, y: sy }}
                    variants={defendingVariants}
                    initial="hidden"
                    animate="visible"
                    className="privacy-policy-defending-container"
                >
                    <Defending className="privacy-policy-defending" />
                </motion.div>

            </motion.div>
        </motion.div>
    );
}

export default PrivacyPolicy;
